import { Component, OnInit } from '@angular/core';
import { LoginService } from '../login.service';

@Component({
  selector: 'app-monthly-claendar',
  templateUrl: './monthly-claendar.component.html',
  styleUrls: ['./monthly-claendar.component.css']
})
export class MonthlyClaendarComponent implements OnInit {

  employees: any[] = [];
  selectedEmployee: number | null = null;
  selectedMonth: string = new Date().toISOString().slice(0, 7); // YYYY-MM

  // attendance data returned from API keyed by YYYY-MM-DD
  monthData: { [date: string]: any } = {};

  // roster data mapped by date (we'll compute in frontend after fetching weekly roster)
  rosterForDate: { [date: string]: any } = {};

  summary: any = null;

  calendar: any[] = []; // array of weeks (each week is array of 7 day objects {date: Date|string} or null)
  weekNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  configured_data: any;
  employee_att: any;
  currentUser: any;

  constructor(private loginService: LoginService) { }

  ngOnInit() {
    this.currentUser = JSON.parse(this.loginService.getLocalSession('currentUser'));
    this.loadEmployees();
  }

  loadEmployees() {
    this.loginService.getEmployeesForAttendance(this.currentUser).subscribe(
      (res: any) => {
        if (res && res.status === 'success') {
          this.employees = res.employees;
          if (this.employees.length) {
            this.selectedEmployee = this.employees[0].id;
          }
          // then load data for month (if employee set)
          if (this.selectedEmployee) this.loadMonthData();
        } else {
          console.error('Failed to load employees', res);
        }
      },
      err => alert('Network Error getEmployeesForAttendance API')
    );
  }

  loadMonthData() {
    if (!this.selectedEmployee || !this.selectedMonth) return;

    // 1) load attendance for month
    this.loginService.getEmployeeMonthData(this.selectedEmployee, this.selectedMonth, this.currentUser)
      .subscribe((res: any) => {
        if (res && res.status === 'success') {
          // API shape: { attendance: {...}, summary: {...} } assumed
          this.monthData = res.attendance || {};
          this.summary = res.summary || {};
        } else {
          this.monthData = {};
          this.summary = {};
        }

        // 2) load weekly roster(s) for the year (we will map by week_no/day)
        this.loginService.getEmployeeMonthlyRoster(this.selectedEmployee, this.selectedMonth, this.currentUser)
          .subscribe((rr: any) => {
            this.getTotals();
            if (rr.status === "success") {
              this.rosterForDate = rr.roster;
            } else {
              this.rosterForDate = {};
            }

            this.generateCalendar();
          }, error => { alert('Network Error getEmployeeMonthlyRoster API'); });
      }, err => {
        alert('Network Error getEmployeeMonthData API');
        console.error('Failed to load month data', err);
        this.monthData = {};
        this.summary = {};
        this.rosterForDate = {};
        this.generateCalendar();
      });
  }

  getTotals() {
    var params = {
      employee_id: this.selectedEmployee,
      month: this.selectedMonth,
      salon_id: this.currentUser.salon_id
    };
    this.loginService.getTotals(params)
      .subscribe((rr: any) => {
        this.configured_data = rr['configured_data'];
        this.employee_att = rr['employee_att'];
      }, err => {
        alert('Network Error getTotals API');
      });
  }

  getDiff(config: number, actual: number): number {
    return config - actual; // positive = extra, negative = shortage
  }

  getDiffClass(diff: number): string {
    return diff < 0 ? 'negative' : 'positive';
  }
  // Normalize roster payload into: { week_no: { Monday: {type,in_time,out_time}, ... } }
  normalizeRosterRaw(raw: any): any {
    const out: any = {};
    if (!raw) return out;

    // If raw is an array of rows
    if (Array.isArray(raw)) {
      raw.forEach((r: any) => {
        const w = r.week_no || r.weekNo || r.week || 0;
        if (!out[w]) out[w] = {};
        out[w][r.day] = { type: r.type, in_time: r.in_time, out_time: r.out_time };
      });
      return out;
    }

    // If already in object form (maybe from backend)
    // e.g., { "12": { "Monday": {...}, ... }, ... }
    return raw;
  }

  // Map roster to actual dates in chosen month. Returns { 'YYYY-MM-DD': {type,in_time,out_time} }
  mapRosterToMonth(rosterByWeek: any, monthIso: string): { [k: string]: any } {
    const [y, m] = monthIso.split('-').map(x => +x);
    const daysInMonth = new Date(y, m, 0).getDate();
    const mapping: any = {};

    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(y, m - 1, d);
      const iso = this.toYMD(dt);
      const dayName = dt.toLocaleString('en-US', { weekday: 'long' }); // Monday etc.
      const weekNo = this.getWeekNumber(dt);

      // find roster for this week number
      const wk = rosterByWeek[weekNo];
      if (wk && wk[dayName]) {
        mapping[iso] = {
          type: wk[dayName].type,
          in_time: wk[dayName].in_time,
          out_time: wk[dayName].out_time
        };
      } else {
        // no roster found: mapping ignored
      }
    }

    return mapping;
  }

  // Generate calendar 6x7 grid (weeks)
  generateCalendar() {
    const [y, m] = this.selectedMonth.split('-').map(x => +x);
    const first = new Date(y, m - 1, 1);
    const startWeekday = first.getDay(); // 0 Sun .. 6 Sat
    const daysInMonth = new Date(y, m, 0).getDate();

    const cal: any[] = [];
    let week: any[] = new Array(7).fill(null);
    let dayCounter = 1;
    let started = false;

    for (let row = 0; row < 6; row++) {
      for (let col = 0; col < 7; col++) {
        if (!started && col === startWeekday) started = true;
        if (started && dayCounter <= daysInMonth) {
          const dateStr = this.toYMD(new Date(y, m - 1, dayCounter));
          week[col] = { date: dateStr };
          dayCounter++;
        } else {
          week[col] = null;
        }
      }
      cal.push(week.slice());
      week = new Array(7).fill(null);
      if (dayCounter > daysInMonth) break;
    }

    this.calendar = cal;
  }

  // convert Date to YYYY-MM-DD
  toYMD(d: Date) {
    const y = d.getFullYear();
    const mm = ('0' + (d.getMonth() + 1)).slice(-2);
    const dd = ('0' + d.getDate()).slice(-2);
    return `${y}-${mm}-${dd}`;
  }

  // ISO week number (1..53) — used to lookup weekly roster
  getWeekNumber(d: Date): number {
    // Copy date so don't modify original
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    // Set to nearest Thursday: current date + 4 - current day number
    const dayNum = date.getUTCDay() || 7; // Sunday=0 => 7
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    // Year of the Thursday
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return weekNo;
  }

  getroster(date) {
    if (!date) return '';
    const cell = this.rosterForDate[date];
    if (!cell || !cell.type) return '';
    switch (cell.type) {
      case 'working': return 'Shift Time ' + this.formatTo12Hour(cell.in_time) + ' - ' + this.formatTo12Hour(cell.out_time);
      case 'week_off': return 'Week Off';
      case 'permission': return 'Permission ' + this.formatTo12Hour(cell.in_time) + ' - ' + this.formatTo12Hour(cell.out_time);
      case 'outdoor_duty': return 'Outdoor Duty ' + this.formatTo12Hour(cell.in_time) + ' - ' + this.formatTo12Hour(cell.out_time);
      case 'holiday': return 'Holiday';
      case 'leave': return 'Absent';
      default: return 'empty';
    }
  }

  formatTo12Hour(time: string): string {
    if (!time) return '';
    let [hour, minute] = time.split(':').map(Number);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    hour = hour ? hour : 12; // 0 → 12
    return `${hour}:${minute.toString().padStart(2, '0')} ${ampm}`;
  }
  // CSS class for cell
  getCellClass(date?: string) {
    if (!date) return 'cell-empty';
    const cell = this.rosterForDate[date];
    if (!cell || !cell.type) return 'cell-empty';
    switch (cell.type) {
      case 'working': return 'cell-scheduled';
      case 'week_off': return 'cell-weekoff';
      case 'permission': return 'cell-permission';
      case 'outdoor_duty': return 'cell-outdoor_duty';
      case 'holiday': return 'cell-holiday';
      case 'leave': return 'cell-absent';
      default: return 'cell-empty';
    }


    // const att = this.monthData[date];
    // if (att) {
    //   if (att.actual_in_time && (!att.actual_out_time || att.actual_out_time === '')) {
    //     return 'cell-pending';
    //   }
    //   switch (att.status) {
    //     case 'working': return 'cell-present';
    //     case 'permission': return 'cell-permission';
    //     case 'week_off': return 'cell-weekoff';
    //     case 'holiday': return 'cell-holiday';
    //     case 'absent': return 'cell-absent';
    //     case 'outdoor_duty': return 'cell-outdoor_duty';
    //     default: return '';
    //   }
    // } else if (this.rosterForDate[date]) {
    //   if (this.rosterForDate[date].type === 'working') return 'cell-scheduled';
    //   if (this.rosterForDate[date].type === 'week_off') return 'cell-weekoff';
    //   return '';
    // }
    // return '';
  }

  // helper checks
  isLate(date: string) {
    const att = this.monthData[date];
    const sched = this.rosterForDate[date];
    if (!att || !att.actual_in_time || !sched || !sched.in_time) return false;
    return att.actual_in_time > sched.in_time;
  }

  isEarlyLeave(date: string, isEarlyLeave) {
    const att = this.monthData[date];
    const sched = this.rosterForDate[date];
    if (!att || !att.actual_out_time || !sched || !sched.out_time) return false;
    return att.actual_out_time < sched.out_time;
  }

  isPendingOut(date: string) {
    const att = this.monthData[date];
    if (!att) return false;
    return att.actual_in_time && (!att.actual_out_time || att.actual_out_time === '00:00:00' || att.actual_out_time === '');
  }

  selectEmployee(id: number) {
    this.selectedEmployee = id;
    this.loadMonthData();
  }
}


// WEBPACK FOOTER //
// ./src/app/monthly-claendar/monthly-claendar.component.ts